/**
 * One account's outstanding statement: what TallyPrime says is open on it, in
 * the shape a customer reads one (the customer page's Statement, its printed
 * statement and its spreadsheet file).
 *
 *   the open bills, oldest first     each with its own dates and how it stands
 * + money owed against no bill       on account, or a ledger kept without bills
 * − the credits                      credit notes and advances, money On Account
 * = the balance                      TallyPrime's closing balance, to the paisa
 *
 * Built from the account as the domain reads it (`interpretReceivables`), so
 * the statement says what Customers, Today and Reports say: the same bills,
 * the same buckets, the same "overdue". Nothing here is estimated. A bill with
 * no due date says so; it is never given one.
 *
 * `ties` is the check a reader makes first: the lines add up to TallyPrime's
 * own balance. It holds by construction for anything `interpretReceivables`
 * produced; a statement that does not tie says so rather than being printed
 * as though it did.
 */
import type { ClosedBill } from './allocationConfidence.js';
import { dateCell, numberCell, textCell, toCsv, type CsvColumn } from './csv.js';
import { fromPaise, toPaise } from './receivablesFacts.js';
import type { AccountPosition, BillView } from './receivablesPosition.js';

/**
 * Where one open bill stands on the statement's day, against TallyPrime's due
 * date (or the bill date, where the company reads a missing due date that way)
 * and the company's grace.
 */
export type BillStanding =
    | { kind: 'overdue'; days: number }
    /** Past its due date, inside the company's grace: not yet overdue for collections. */
    | { kind: 'grace'; days: number }
    | { kind: 'due_today' }
    | { kind: 'not_due'; inDays: number }
    /** TallyPrime gave no due date, and the company does not read the bill date as one. */
    | { kind: 'no_due_date' };

/** Where the company's ageing puts a bill: one of its buckets, or beside them. */
export type StatementAge =
    | { kind: 'bucket'; index: number; of: number; label: string }
    | { kind: 'not_yet_due' }
    | { kind: 'no_due_date' };

export interface StatementBill {
    key: string;
    ref: string;
    billDate: string;
    dueDate: string | null;
    /** What is still pending on the bill, as TallyPrime states it. Positive. */
    pending: number;
    ageDays: number;
    standing: BillStanding;
    age: StatementAge;
}

export interface StatementCredit {
    key: string;
    /** The credit's reference; empty for money On Account, which has none. */
    ref: string;
    /** The credit's own date; null for money On Account, which TallyPrime keeps in no bill. */
    date: string | null;
    /** Positive: what the credit takes off the balance. */
    amount: number;
    kind: 'credit_bill' | 'on_account' | 'not_bill_wise';
}

export interface StatementCleared {
    key: string;
    ref: string;
    billDate: string;
    /** What was pending on the bill when Timely last saw it open. */
    amount: number;
    /** The day of the read that first found it closed in TallyPrime. */
    clearedOn: string;
}

export interface AccountStatement {
    /** The day TallyPrime stated these figures for. */
    asOf: string;
    billWise: boolean;
    /** Open debit bills, oldest first. */
    bills: StatementBill[];
    /** Credit bills by date, then money On Account. */
    credits: StatementCredit[];
    /** Money owed against no bill (positive), and TallyPrime's reason for it. */
    unbilledDebit: { amount: number; reason: 'on_account' | 'not_bill_wise' } | null;
    totals: {
        bills: number;
        billCount: number;
        /** Overdue for collections: past the due date and the company's grace. */
        overdue: number;
        overdueCount: number;
        /** Past the due date, whatever the grace: what the customer reads as overdue. */
        pastDue: number;
        pastDueCount: number;
        credits: number;
        /** Signed, + = Dr (owed to the company): TallyPrime's closing balance. */
        balance: number;
    };
    /** The most days any open bill is past its due date; null when none is. */
    oldestOverdueDays: number | null;
    /** Bills Timely saw close in TallyPrime, newest first. */
    cleared: StatementCleared[];
    /** The lines add up to TallyPrime's balance, to the paisa. */
    ties: boolean;
}

function standingOf(bill: BillView): BillStanding {
    // Days past the due date: TallyPrime's, or the bill date where the company reads a missing due date as the bill
    // date (then `collectionOverdueFrom` is set without one) — the same reading `interpretReceivables` makes.
    const days = bill.overdueDays ?? (bill.collectionOverdueFrom !== null ? bill.ageDays : null);
    if (days === null) return { kind: 'no_due_date' };
    if (bill.overdueForCollections) return { kind: 'overdue', days };
    if (days > 0) return { kind: 'grace', days };
    if (days === 0) return { kind: 'due_today' };
    return { kind: 'not_due', inDays: -days };
}

function ageOf(bill: BillView, buckets: number): StatementAge {
    const p = bill.placement;
    if (p?.kind === 'bucket') return { kind: 'bucket', index: p.bucket, of: buckets, label: bill.bucketLabel ?? '' };
    if (p?.kind === 'not_yet_due') return { kind: 'not_yet_due' };
    return { kind: 'no_due_date' };
}

/** Oldest first, as a statement is read; the reference, then the key, keep bills of one day in one order. */
const byDate = (a: { date: string; ref: string; key: string }, b: { date: string; ref: string; key: string }) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : a.key < b.key ? -1 : a.key > b.key ? 1 : 0;

export function accountStatement(account: AccountPosition, closedBills: readonly Omit<ClosedBill, 'sourceKey'>[] = [], asOf = ''): AccountStatement {
    const buckets = account.buckets.length;
    const bills: StatementBill[] = account.bills
        .filter(b => b.side === 'Dr')
        .map(b => ({
            key: b.key, ref: b.ref, billDate: b.billDate, dueDate: b.dueDate ?? null, pending: b.pending, ageDays: b.ageDays,
            standing: standingOf(b), age: ageOf(b, buckets),
        }))
        .sort((a, b) => byDate({ date: a.billDate, ref: a.ref, key: a.key }, { date: b.billDate, ref: b.ref, key: b.key }));

    const credits: StatementCredit[] = account.bills
        .filter(b => b.side === 'Cr')
        .map(b => ({ key: b.key, ref: b.ref, date: b.billDate, amount: fromPaise(-toPaise(b.pending)), kind: 'credit_bill' as const }))
        .sort((a, b) => byDate({ date: a.date, ref: a.ref, key: a.key }, { date: b.date, ref: b.ref, key: b.key }));
    const unbilledP = toPaise(account.unbilled);
    const reason = account.unbilledReason ?? (account.billWise ? 'on_account' : 'not_bill_wise');
    if (unbilledP < 0) credits.push({ key: `unbilled:${reason}`, ref: '', date: null, amount: fromPaise(-unbilledP), kind: reason });

    const billsP = bills.reduce((s, b) => s + toPaise(b.pending), 0);
    const creditsP = credits.reduce((s, c) => s + toPaise(c.amount), 0);
    const overdue = bills.filter(b => b.standing.kind === 'overdue');
    const pastDue = bills.filter(b => b.standing.kind === 'overdue' || b.standing.kind === 'grace');
    const balanceP = billsP - creditsP + Math.max(unbilledP, 0);

    const cleared = closedBills
        // Stored as JSON: a bill remembered before keys were stored with it is known by its reference.
        .map(c => ({ key: c.key || `ref:${c.ref}`, ref: c.ref, billDate: c.billDate, amount: Number(c.amount), clearedOn: c.closedAt }))
        .sort((a, b) => (a.clearedOn !== b.clearedOn ? (a.clearedOn < b.clearedOn ? 1 : -1) : -byDate(
            { date: a.billDate, ref: a.ref, key: a.key }, { date: b.billDate, ref: b.ref, key: b.key })));

    return {
        asOf,
        billWise: account.billWise,
        bills,
        credits,
        unbilledDebit: unbilledP > 0 ? { amount: fromPaise(unbilledP), reason } : null,
        totals: {
            bills: fromPaise(billsP),
            billCount: bills.length,
            overdue: fromPaise(overdue.reduce((s, b) => s + toPaise(b.pending), 0)),
            overdueCount: overdue.length,
            pastDue: fromPaise(pastDue.reduce((s, b) => s + toPaise(b.pending), 0)),
            pastDueCount: pastDue.length,
            credits: fromPaise(creditsP),
            balance: fromPaise(toPaise(account.closing)),
        },
        oldestOverdueDays: account.oldestOverdueDays,
        cleared,
        ties: balanceP === toPaise(account.closing),
    };
}

/* ------------------------------ the spreadsheet ----------------------------- */

/** How a statement line is named in the file and on the printed statement. */
export const STATEMENT_LINE_WORDS = {
    bill: 'Bill',
    credit_bill: 'Credit note or advance',
    on_account: 'Received On Account',
    not_bill_wise: 'Balance kept without bills',
    owed_on_account: 'Owed against no bill',
    balance: 'Balance',
} as const;

/** A bill's standing in words: "45 days overdue", "Due in 3 days". */
export function standingWords(s: BillStanding): string {
    const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
    switch (s.kind) {
        case 'overdue': return `${days(s.days)} overdue`;
        case 'grace': return `${days(s.days)} past due, within grace`;
        case 'due_today': return 'Due today';
        case 'not_due': return `Due in ${days(s.inDays)}`;
        case 'no_due_date': return 'No due date';
    }
}

/**
 * The same, as the customer reads it, on paper and in the file: a bill past its
 * due date is overdue to them, whatever grace the company allows before it
 * chases one, and a bill with no due date says nothing rather than something
 * invented.
 */
export function customerStandingWords(s: BillStanding): string {
    if (s.kind === 'grace') return standingWords({ kind: 'overdue', days: s.days });
    if (s.kind === 'no_due_date') return '';
    return standingWords(s);
}

/** A bill's place in the company's ageing, in words. */
export const ageWords = (a: StatementAge): string =>
    a.kind === 'bucket' ? a.label : a.kind === 'not_yet_due' ? 'Not yet due' : 'No due date';

interface CsvLine {
    type: string; ref: string; date: string; due: string; daysOverdue: number | null; status: string; age: string; amount: number;
}

/**
 * The statement as a spreadsheet: one row per bill, then the credits as
 * negative amounts, then the balance they make. Amounts are numbers in the
 * company's currency, said once in the heading (`csv.ts`), so a column adds up
 * to the balance row in any spreadsheet.
 */
export function statementCsv(s: AccountStatement, currency: string): string {
    const columns: CsvColumn<CsvLine>[] = [
        { header: 'Type', cell: r => textCell(r.type) },
        { header: 'Reference', cell: r => textCell(r.ref) },
        { header: 'Date', cell: r => dateCell(r.date) },
        { header: 'Due date', cell: r => dateCell(r.due) },
        { header: 'Days overdue', cell: r => numberCell(r.daysOverdue) },
        { header: 'Status', cell: r => textCell(r.status) },
        { header: 'Age', cell: r => textCell(r.age) },
        { header: `Amount (${currency})`, cell: r => numberCell(r.amount) },
    ];
    const rows: CsvLine[] = [
        ...s.bills.map(b => ({
            type: STATEMENT_LINE_WORDS.bill, ref: b.ref, date: b.billDate, due: b.dueDate ?? '',
            daysOverdue: b.standing.kind === 'overdue' || b.standing.kind === 'grace' ? b.standing.days : null,
            status: customerStandingWords(b.standing), age: ageWords(b.age), amount: b.pending,
        })),
        ...(s.unbilledDebit ? [{
            type: s.unbilledDebit.reason === 'not_bill_wise' ? STATEMENT_LINE_WORDS.not_bill_wise : STATEMENT_LINE_WORDS.owed_on_account,
            ref: '', date: '', due: '', daysOverdue: null, status: '', age: '', amount: s.unbilledDebit.amount,
        }] : []),
        ...s.credits.map(c => ({
            type: STATEMENT_LINE_WORDS[c.kind], ref: c.ref, date: c.date ?? '', due: '', daysOverdue: null, status: '', age: '',
            amount: fromPaise(-toPaise(c.amount)),
        })),
        { type: STATEMENT_LINE_WORDS.balance, ref: '', date: s.asOf, due: '', daysOverdue: null, status: '', age: '', amount: s.totals.balance },
    ];
    return toCsv(columns, rows);
}
